import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can sign in now");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080B11] flex items-center justify-center p-6 relative" data-testid="reset-password-page">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <img src="/kleeng-logo.png" alt="Kleen-G" className="w-10 h-10 rounded-md" />
          <span className="font-display font-black text-xl tracking-tight">Kleen-G</span>
        </div>

        <div className="hud-card accent-top rounded-xl p-8" style={{ "--accent-line": "#06B6D4" }}>
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
              <h1 className="mt-4 font-display text-xl font-bold">Password reset complete</h1>
              <p className="mt-2 text-sm text-slate-400">Your password has been updated. Sign in with your new credentials.</p>
              <button
                data-testid="reset-goto-login-btn"
                onClick={() => navigate("/login")}
                className="mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-md bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors"
              >
                Go to Sign In <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : !token ? (
            <div className="text-center" data-testid="reset-no-token">
              <KeyRound className="w-12 h-12 text-amber-400 mx-auto" />
              <h1 className="mt-4 font-display text-xl font-bold">Invalid reset link</h1>
              <p className="mt-2 text-sm text-slate-400">This link is missing its token. Request a fresh reset link from the sign-in page.</p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-cyan-400 text-sm font-mono hover:text-cyan-300">Back to sign in <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-cyan-400" />
                <h1 className="font-display text-xl font-bold">Set a new password</h1>
              </div>
              <p className="text-xs text-slate-500 mb-6">Minimum 8 characters, with at least one letter and one number.</p>
              <form onSubmit={submit} className="space-y-4">
                <Field label="New Password" type="password" testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <Field label="Confirm Password" type="password" testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                {error && <div data-testid="reset-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{error}</div>}
                <button
                  type="submit"
                  data-testid="reset-submit-btn"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update Password <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400 mb-1.5 block">{label}</label>
      <input
        data-testid={testid}
        {...props}
        className="w-full px-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60 transition-colors"
      />
    </div>
  );
}
